import type { FanoutSlice } from '~/api/hooks/useTimeframeFanout'

export interface RankedDimRow {
  key: string
  value: number
}

const TOTAL = 'total'

/** Raw dim-combo key for one walk row. Nulls (a side that can't carry a dim, mig 0080)
 *  are dropped so the key reflects only the dims this side actually partitions by.
 *  No dims (null/{}) → the ungrouped 'total' sentinel. Keys stay RAW (V1 invariant).
 *  Relies on consistent dims key order from walk_* SQL (same logical combo must arrive with keys in same order). */
export function dimComboKey(dims: Record<string, string | null> | null | undefined): string {
  if (!dims) return TOTAL
  const vals = Object.values(dims).filter((v): v is string => v != null)
  return vals.length > 0 ? vals.join(' / ') : TOTAL
}

/** One timeframe slice → ranked rows: sum per dim-combo, skip null values, sort value desc. */
export function toRankedDimRows(slice: FanoutSlice): RankedDimRow[] {
  const byKey = new Map<string, number>()
  for (const r of slice.rows) {
    if (r.value == null) continue
    const key = dimComboKey(r.dims)
    byKey.set(key, (byKey.get(key) ?? 0) + r.value)
  }
  return [...byKey.entries()]
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => b.value - a.value)
}

/** Union of non-total dim-value keys across slices (feeds the shared color map).
 *  Skips rows with value == null to ensure the color map covers only keys that render as bars (consistent with toRankedDimRows). */
export function collectDimValueKeys(slices: FanoutSlice[]): string[] {
  const keys = new Set<string>()
  for (const s of slices) {
    for (const r of s.rows) {
      if (r.value == null) continue
      const key = dimComboKey(r.dims)
      if (key !== TOTAL) keys.add(key)
    }
  }
  return [...keys]
}

/** An arm is "grouped" when its resolved data carries any dim-value key. */
export function isGrouped(slices: FanoutSlice[]): boolean {
  return collectDimValueKeys(slices).length > 0
}
